import type { ExtraStore } from "../services/UserService";
import { formatExtraStoreId } from "./extraStores";

/**
 * Plan extra stores a member hid from the store picker, per retailer.
 *
 * Contract (PATCH /v1/user `hiddenExtraStores`, mirrored by pinto):
 * `{ "walmart": ["3858"], "home_depot": ["450"] }`. Keys are the retailers
 * that have extra stores; IDs are digit strings without leading zeros. The
 * whole map is replaced on every PATCH. Servers that store it always return
 * the field on the user (`{}` when empty); older servers omit it and reject
 * the PATCH with 400, so the value is also cached per user in localStorage
 * and pushed once the server accepts it.
 */
export type HiddenExtraStoreMap = Partial<Record<ExtraStoreRetailer, string[]>>;

export const EXTRA_STORE_RETAILERS = ["home_depot", "walmart"] as const;
export type ExtraStoreRetailer = (typeof EXTRA_STORE_RETAILERS)[number];

/** Same cap as the server (server/pkg/user/hidden_extra_stores.go). */
export const MAX_HIDDEN_EXTRA_STORES_PER_RETAILER = 50;

const NO_HIDDEN: ReadonlySet<string> = new Set();

export function isExtraStoreRetailer(retailer: string | null | undefined): retailer is ExtraStoreRetailer {
  return retailer === "home_depot" || retailer === "walmart";
}

/** Drops unknown retailers and invalid IDs; IDs are de-duplicated and sorted. */
export function normalizeHiddenExtraStores(raw: unknown): HiddenExtraStoreMap {
  const out: HiddenExtraStoreMap = {};
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return out;
  for (const retailer of EXTRA_STORE_RETAILERS) {
    const list = (raw as Record<string, unknown>)[retailer];
    if (!Array.isArray(list)) continue;
    const ids = new Set<string>();
    for (const value of list) {
      if (typeof value !== "string" && typeof value !== "number") continue;
      const id = formatExtraStoreId(String(value));
      if (/^\d{1,12}$/.test(id)) ids.add(id);
    }
    const sorted = [...ids].sort().slice(0, MAX_HIDDEN_EXTRA_STORES_PER_RETAILER);
    if (sorted.length) out[retailer] = sorted;
  }
  return out;
}

export function sameHiddenExtraStores(a: HiddenExtraStoreMap, b: HiddenExtraStoreMap): boolean {
  return EXTRA_STORE_RETAILERS.every((retailer) => (a[retailer] ?? []).join(",") === (b[retailer] ?? []).join(","));
}

export function hiddenExtraStoreIds(
  map: HiddenExtraStoreMap | null | undefined,
  retailer: string | null | undefined
): ReadonlySet<string> {
  if (!map || !isExtraStoreRetailer(retailer)) return NO_HIDDEN;
  const ids = map[retailer];
  return ids?.length ? new Set(ids) : NO_HIDDEN;
}

/** Returns the map with one store hidden (hidden=true) or shown again. */
export function setExtraStoreHidden(
  map: HiddenExtraStoreMap,
  retailer: string,
  storeId: string,
  hidden: boolean
): HiddenExtraStoreMap {
  const id = formatExtraStoreId(storeId);
  if (!isExtraStoreRetailer(retailer) || !id) return map;
  const current = map[retailer] ?? [];
  const has = current.includes(id);
  if (has === hidden) return map;
  if (hidden && current.length >= MAX_HIDDEN_EXTRA_STORES_PER_RETAILER) return map;
  const next = hidden ? [...current, id].sort() : current.filter((value) => value !== id);
  const out = { ...map };
  if (next.length) out[retailer] = next;
  else delete out[retailer];
  return out;
}

/** Splits the plan's extra stores into the ones shown and the ones hidden. */
export function splitExtraStores<T extends Pick<ExtraStore, "storeId">>(
  extras: readonly T[],
  hidden: ReadonlySet<string>
): { visible: T[]; hidden: T[] } {
  const visible: T[] = [];
  const hiddenOut: T[] = [];
  for (const extra of extras) {
    (hidden.has(formatExtraStoreId(extra.storeId)) ? hiddenOut : visible).push(extra);
  }
  return { visible, hidden: hiddenOut };
}

/**
 * IDs of the user's current extra stores that are hidden. Only these are
 * filtered out of feeds: a stale hidden ID that later became a saved store (or
 * stopped being an extra) must never hide that store's deals.
 */
export function activeHiddenExtraStoreIds(
  extras: readonly Pick<ExtraStore, "storeId">[],
  hidden: ReadonlySet<string>
): ReadonlySet<string> {
  if (!hidden.size || !extras.length) return NO_HIDDEN;
  const out = new Set<string>();
  for (const extra of extras) {
    const id = formatExtraStoreId(extra.storeId);
    if (hidden.has(id)) out.add(id);
  }
  return out.size ? out : NO_HIDDEN;
}

/**
 * Removes rows that came from a hidden extra store. Servers before the
 * hiddenExtraStores release still put those stores in the "All" feed.
 */
export function excludeHiddenExtraStoreProducts<T extends { storeId?: string | number | null }>(
  products: T[],
  activeHidden: ReadonlySet<string>
): T[] {
  if (!activeHidden.size) return products;
  // Row store IDs can carry a suffix ("1234-CRAZY-DEALS", "0450-PENNY-ITEM").
  const baseId = (storeId: string | number | null | undefined) => formatExtraStoreId(String(storeId ?? "").split("-")[0]);
  const out = products.filter((product) => !activeHidden.has(baseId(product.storeId)));
  return out.length === products.length ? products : out;
}

/** Store to switch to after hiding storeId: "all" when it was the selected one. */
export function storeAfterHidingExtraStore(selectedStoreId: string, hiddenStoreId: string): string {
  return formatExtraStoreId(selectedStoreId) === formatExtraStoreId(hiddenStoreId) ? "all" : selectedStoreId;
}

/** Locally cached preference. `pending` means the server has not stored it yet. */
export interface LocalHiddenExtraStores {
  stores: HiddenExtraStoreMap;
  pending: boolean;
  /** Set once a server accepted or returned the field for this user. */
  synced?: boolean;
}

/**
 * Combines the server value (undefined when the server predates the field)
 * with the local cache.
 * - Old server: the local value is the only source.
 * - Local edits not yet stored: local wins, except the first time a server
 *   supports the field, when both sides are united so hides made on other
 *   devices before the server release are not lost.
 * - Otherwise the server value wins (it reflects edits from other devices).
 */
export function resolveHiddenExtraStores(
  serverValue: unknown,
  local: LocalHiddenExtraStores | null
): { stores: HiddenExtraStoreMap; push: boolean; local: LocalHiddenExtraStores } {
  const localStores = normalizeHiddenExtraStores(local?.stores);
  const serverSupports = !!serverValue && typeof serverValue === "object" && !Array.isArray(serverValue);
  if (!serverSupports) {
    const next = { stores: localStores, pending: local?.pending ?? false, synced: local?.synced };
    return { stores: localStores, push: false, local: next };
  }
  const serverStores = normalizeHiddenExtraStores(serverValue);
  if (local?.pending) {
    let stores = localStores;
    if (!local.synced) {
      stores = { ...serverStores };
      for (const retailer of EXTRA_STORE_RETAILERS) {
        for (const id of localStores[retailer] ?? []) stores = setExtraStoreHidden(stores, retailer, id, true);
      }
    }
    const push = !sameHiddenExtraStores(stores, serverStores);
    return { stores, push, local: { stores, pending: push, synced: true } };
  }
  return { stores: serverStores, push: false, local: { stores: serverStores, pending: false, synced: true } };
}

export const hiddenExtraStoresStorageKey = (userId: string) => `dealhawk_hidden_extra_stores:${userId}`;

export function readLocalHiddenExtraStores(userId: string): LocalHiddenExtraStores | null {
  try {
    const raw = localStorage.getItem(hiddenExtraStoresStorageKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<LocalHiddenExtraStores> | null;
    if (!parsed || typeof parsed !== "object") return null;
    return { stores: normalizeHiddenExtraStores(parsed.stores), pending: parsed.pending === true, synced: parsed.synced === true };
  } catch {
    return null;
  }
}

export function writeLocalHiddenExtraStores(userId: string, value: LocalHiddenExtraStores): void {
  try {
    localStorage.setItem(hiddenExtraStoresStorageKey(userId), JSON.stringify(value));
  } catch {
    /* storage unavailable (private mode): keep it for this session */
  }
}
